// ============================================================
// Layout principal con menú hamburguesa móvil
// ============================================================
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/utils/api'
import { Outlet, Link, useNavigate } from 'react-router-dom'
import { useCartStore, useAuthStore, useUIStore } from '@/store'
import CartDrawer from '@/components/cart/CartDrawer'
import Notifications from '@/components/shared/Notifications'
import styles from './Layout.module.css'
import AnnouncementBar from '@/components/shared/AnnouncementBar'
import BrandLogo from '@/components/shared/BrandLogo'
import { useBranding } from '@/hooks/useSiteConfig'

const TICKER_ITEMS = [
  'Nueva colección primavera 2025',
  'Envíos gratis en compras mayores a $1,500',
  'Mayoreo desde 12 piezas — 20% de descuento',
  'Devoluciones gratuitas en 30 días',
  'Facturación CFDI incluida',
  'Pago con MercadoPago y OXXO',
]

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const cartCount  = useCartStore(s => s.itemCount())
  const toggleCart = useCartStore(s => s.toggleCart)
  const user       = useAuthStore(s => s.user)
  const logout     = useAuthStore(s => s.logout)
  const isAdmin    = useAuthStore(s => s.isAdmin)
  const navigate   = useNavigate()
  const brand      = useBranding()

  
  const handleLogout = () => {
    logout()
    navigate('/')
    setMobileOpen(false)
  }
	const { data: siteConfig } = useQuery({
	  queryKey: ['site-config'],
	  queryFn:  () => api.get('/site-config').then(r => r.data),
	})
	const social = siteConfig?.social
	const whatsapp = siteConfig?.social?.whatsapp

  // Menú principal: el configurado en Admin → Contenido → Menú, o uno por defecto
  const navItems: { label: string; url: string }[] = siteConfig?.nav?.length > 0 ? siteConfig.nav : [
    { label:'Hombre',          url:'/hombre'      },
    { label:'Mujer',           url:'/mujer'       },
    { label:'Colecciones',     url:'/colecciones' },
    { label:'Mayoreo',         url:'/mayoreo'     },
    { label:'Outlet',          url:'/outlet'      },
    { label:'Rastrear pedido', url:'/rastrear'    },
  ]

  return (
    <div className={styles.root}>

      {/* Ticker dinamico desde config del sitio */}
	  <AnnouncementBar />

      {/* Navbar */}
      <header className={styles.nav}>
       <Link to="/" className={styles.logo}>
		  <BrandLogo variant="auto" />
		</Link>

        {/* Links desktop */}
        <nav className={styles.navLinks} aria-label="Navegación principal">
          {navItems.map((item: any) => (
		  <Link key={item.url} to={item.url}>{item.label}</Link>
		))}
        </nav>

        <div className={styles.navActions}>
          {/* Desktop: iconos de usuario */}
          <div className={styles.navLinks} style={{ gap:4 }}>
            {user ? (
              <>
			  
			
                <Link to="/mi-cuenta" className={styles.iconBtn} aria-label="Mi cuenta">
                  <i className="ti ti-user-check" style={{ fontSize:22 }}/>
                </Link>
                {isAdmin() && (
                  <Link to="/admin" className={styles.iconBtn} aria-label="Admin">
                    <i className="ti ti-layout-dashboard"/>
                  </Link>
                )}
              </>
            ) : (
              <Link to="/login" className={styles.iconBtn} aria-label="Iniciar sesión">
                <i className="ti ti-user" style={{ fontSize:22 }}/>
              </Link>
            )}
          </div>

			{/* Redes sociales */}
			{social && (Object.values(social).some(v => v)) && (
			  <div style={{ display:'flex', gap:10, paddingRight:12,
				borderRight:'1px solid var(--line2)', marginRight:4 }}>
				{social.facebook  && <a href={social.facebook}  target="_blank" rel="noopener noreferrer" style={{ color:'var(--white)', fontSize:18, lineHeight:1, display:'flex' }}><i className="ti ti-brand-facebook"/></a>}
				{social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" style={{ color:'var(--white)', fontSize:18, lineHeight:1, display:'flex' }}><i className="ti ti-brand-instagram"/></a>}
				{social.youtube   && <a href={social.youtube}   target="_blank" rel="noopener noreferrer" style={{ color:'var(--white)', fontSize:18, lineHeight:1, display:'flex' }}><i className="ti ti-brand-youtube"/></a>}
				{social.tiktok    && <a href={social.tiktok}    target="_blank" rel="noopener noreferrer" style={{ color:'var(--white)', fontSize:18, lineHeight:1, display:'flex' }}><i className="ti ti-brand-tiktok"/></a>}
				{social.whatsapp  && <a href={social.whatsapp}  target="_blank" rel="noopener noreferrer" style={{ color:'var(--white)', fontSize:18, lineHeight:1, display:'flex' }}><i className="ti ti-brand-whatsapp"/></a>}
			  </div>
			)}

          {/* Carrito — siempre visible */}
          <button onClick={toggleCart} className={styles.cartBtn} aria-label={`Carrito ${cartCount}`}>
            <i className="ti ti-shopping-bag"/>
            {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
          </button>

          {/* Hamburguesa — solo móvil */}
          <button
            className={styles.menuBtn}
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Menú"
            aria-expanded={mobileOpen}
          >
            <i className={`ti ${mobileOpen ? 'ti-x' : 'ti-menu-2'}`}/>
          </button>
        </div>
      </header>

      {/* Menú móvil desplegable */}
      <nav className={`${styles.mobileMenu} ${mobileOpen ? styles.open : ''}`}
           aria-label="Navegación móvil">
        {/* Mismos enlaces que el menú de escritorio (Admin → Contenido → Menú) */}
        {navItems.map((item: any) => (
          <Link key={item.url} to={item.url} onClick={() => setMobileOpen(false)}>{item.label}</Link>
        ))}
        {!navItems.some((i: any) => i.url === '/rastrear') && (
          <Link to="/rastrear" onClick={() => setMobileOpen(false)}>Rastrear pedido</Link>
        )}
        {user ? (
          <>
            <Link to="/mi-cuenta" onClick={() => setMobileOpen(false)}>Mi cuenta</Link>
            {isAdmin() && (
              <Link to="/admin" onClick={() => setMobileOpen(false)}>Panel Admin</Link>
            )}
            <button onClick={handleLogout}>Cerrar sesión</button>
          </>
        ) : (
          <Link to="/login" onClick={() => setMobileOpen(false)}>Iniciar sesión</Link>
        )}
      </nav>

      {/* Contenido */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerGrid}>
          <div>
            <div className={styles.footerLogo}><BrandLogo /></div>
            {brand.footerText && (
              <p className={styles.footerDesc} style={{ whiteSpace:'pre-line' }}>
                {brand.footerText}
              </p>
            )}
          </div>
          <div>
            <div className={styles.footerTitle}>Tienda</div>
            <Link to="/hombre">Hombre</Link>
            <Link to="/mujer">Mujer</Link>
            <Link to="/colecciones">Colecciones</Link>
            <Link to="/mayoreo">Mayoreo</Link>
            <Link to="/outlet">Outlet</Link>
          </div>
          <div>
            <div className={styles.footerTitle}>Ayuda</div>
            <Link to="/rastrear">Rastrear pedido</Link>
            <a href="#">Guía de tallas</a>
            <a href="#">Envíos y devoluciones</a>
            <a href="#">Preguntas frecuentes</a>
            <a href="#">Facturación CFDI</a>
          </div>
          <div>
            <div className={styles.footerTitle}>Legal</div>
            <a href="#">Aviso de privacidad</a>
            <a href="#">Términos y condiciones</a>
            <a href="#">Política de cookies</a>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} {brand.name}{brand.location ? ` · ${brand.location}` : ''}</span>
          <div className={styles.social}>
            <a href="#" aria-label="Instagram"><i className="ti ti-brand-instagram"/></a>
            <a href="#" aria-label="TikTok"><i className="ti ti-brand-tiktok"/></a>
            <a href="#" aria-label="Facebook"><i className="ti ti-brand-facebook"/></a>
          </div>
        </div>
     </footer>
      <CartDrawer/>
      <Notifications/>
      {/* Botón flotante WhatsApp */}
      {whatsapp && (
        <a href={`${whatsapp}?text=${encodeURIComponent(`Hola, me interesa un producto de ${brand.name}. ¿Me pueden ayudar?`)}`}
          style={{ position:'fixed', bottom:24, right:24, zIndex:999,
            width:56, height:56, borderRadius:'50%',
            background:'#25D366', color:'#fff',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:28, boxShadow:'0 4px 20px rgba(37,211,102,.4)',
            textDecoration:'none', transition:'transform .2s, box-shadow .2s' }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.1)'
            e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,211,102,.6)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)'
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,.4)'
          }}>
          <i className="ti ti-brand-whatsapp"/>
        </a>
      )}
    </div>
  )
}